import type { VisitorPageInput } from "@/lib/graphql/generated/types";
import { isTrustedMessageSource } from "@/lib/message-source";
import { buildVisitorPage, type RawHostPage } from "./build-visitor-page";

/**
 * iframe embed: the frame's own location is not the host's, so the loader's `?referer=` (initial url)
 * and `wexio:host-navigation:v1` messages (url + title + referrer) are the source. The listener is
 * registered when this module loads, so the loader's first message is never missed.
 */
let iframeHost: RawHostPage | null = null;

function onMessage(e: MessageEvent) {
  if (!isTrustedMessageSource(e, "iframe")) return;
  const d = e.data;
  if (!d || typeof d !== "object" || d.type !== "wexio:host-navigation:v1") return;
  if (typeof d.url !== "string") return;
  iframeHost = {
    url: d.url,
    title: typeof d.title === "string" ? d.title : undefined,
    referrer: typeof d.referrer === "string" ? d.referrer : undefined,
    description: typeof d.description === "string" ? d.description : undefined,
  };
}

if (typeof window !== "undefined") window.addEventListener("message", onMessage);

/** Test seam. */
export function resetHostPageSnapshot() {
  iframeHost = null;
}

function metaDescription(): string | undefined {
  const named = document.querySelector('meta[name="description"]')?.getAttribute("content")?.trim();
  if (named) return named;
  return (
    document.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? undefined
  );
}

/**
 * The `page` for a send, read AT THIS MOMENT (a queued send keeps the page it was made on, even if
 * it replays after a navigation). `undefined` when there is no http/https host url.
 */
export function captureVisitorPage(kind: "iframe" | "web-component"): VisitorPageInput | undefined {
  if (typeof window === "undefined") return undefined;
  if (kind === "iframe") {
    const referer = new URLSearchParams(window.location.search).get("referer") ?? undefined;
    return buildVisitorPage(iframeHost ?? { url: referer });
  }
  return buildVisitorPage({
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    description: metaDescription(),
  });
}
